import { palette } from "@brexhq/metal-foundation-shared";
import React, { useState } from "react";
import { useInView } from "react-intersection-observer";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { TopSpacing } from "@brexhq/next/src/components/layout/TopSpacing/TopSpacing";
import { footerExperiment } from "@brexhq/next/src/config/experimentation/data";
import { getFooterById } from "@brexhq/next/src/data/dataUtils";
import {
  ExperimentAnalyticsData,
  useTrackStatsigIfExperiment,
} from "@brexhq/next/src/hooks/analytics/useTrackStatsig";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import useIsomorphicLayoutEffect from "@brexhq/next/src/hooks/useIsomorphicLayoutEffect";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import { statsigClientSide } from "@brexhq/next/src/utils/experimentation/statsig";

import { GridWidth } from "../Grid";

import { FooterLoadingStencils } from "./FooterLoading";
import { FooterMenu } from "./FooterMenu";
import { LegalCopy } from "./LegalCopy";
import { FooterData, FooterType } from "./types";
import { trackFooterViewIfNeeded } from "./utils";

const Footer: React.FC<FooterType> = ({
  data,
  topSpacing = "none",
  additionalLegalFooterSection,
  hideFooterLinks,
  hideFooterSecondaryLinks,
}) => {
  const { pathname, search } = useLocation();
  const { trackEvent } = useAnalytics();
  const [ref, inView] = useInView({ triggerOnce: true });

  const [isFooterLoading, setIsFooterLoading] = useState(
    Boolean(footerExperiment),
  );
  const [footerData, setFooterData] = React.useState<FooterData>(data);
  const [analyticsData, setAnalyticsData] =
    React.useState<ExperimentAnalyticsData | null>(null);

  useIsomorphicLayoutEffect(() => {
    const getFooterExp = async () => {
      if (!footerExperiment) return;

      const {
        statsigExperimentId,
        references,
        parameterKey,
        defaultParameterValue,
        experimentStatus,
      } = footerExperiment;

      const { value } = await statsigClientSide({
        statsigExperimentId,
        parameterKey: parameterKey,
        defaultParameterValue: defaultParameterValue,
      });

      const footer = references[value];

      setIsFooterLoading(false);
      setFooterData(getFooterById(footer.docId));
      setAnalyticsData({
        experimentId: statsigExperimentId,
        experimentType: "footer",
        variation: footer.variation,
        experimentStatus,
      });
    };

    getFooterExp();
  }, []);

  // Only track footer view and experiment view event if footer is inView
  useTrackStatsigIfExperiment(inView ? analyticsData : null);
  trackFooterViewIfNeeded(pathname, search, inView, trackEvent);

  const FooterContent = () => (
    <>
      {!hideFooterLinks && footerData.primaryMenu
        ? footerData.primaryMenu.map((row) => (
            <FooterMenu key={row._key} {...row} />
          ))
        : null}

      {!hideFooterSecondaryLinks && footerData.secondaryMenu
        ? footerData.secondaryMenu.map((row) => (
            <FooterMenu key={row._key} {...row} />
          ))
        : null}
    </>
  );

  return (
    <TopSpacing topSpacing={topSpacing}>
      <Container
        ref={ref}
        as="footer"
        padding={["xxlrg", 0]}
        style={{
          background: palette.slateBlue[12],
        }}
      >
        <nav>
          {isFooterLoading ? <FooterLoadingStencils /> : <FooterContent />}
          <GridWidth>
            <Container padding={["xlrg", 0, 0, 0]}>
              <LegalCopy
                additionalLegalFooterSection={additionalLegalFooterSection}
              />
            </Container>
          </GridWidth>
        </nav>
      </Container>
    </TopSpacing>
  );
};

export default Footer;
